import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ClipLoader } from 'react-spinners';

import { getPromoter } from '../../redux/actions/promoter';
import { getBrand } from '../../redux/actions/brands';
import { getUnreadNotificationsCount } from '../../redux/actions/notifications';
import { hasEasypayPayoutAccount } from '../../utils/easypayPayout';

import Sidebar from './Sidebar';
import PromoterInfo from './PromoterInfo';
import BrandInfo from './BrandInfo';
import Notifications from './Notifications';
import Messages from './Messages';
import MyEvents from './MyEvents';
import PaymentDetails from './PaymentDetails';
import Financials from './Financials';
import Preferences from './Preferences';
import Analytics from './Analytics';
import SecurityPrivacy from './SecurityPrivacy';
import Help from './Help';
import BoardHeader from './Boardheader';
import PromoterInfoEdit from './PromoterInfoEdit';
import BrandInfoEdit from './BrandInfoEdit';
import ShareEventModal from 'components/ShareEventModal';

const Dashboard = () => {
  const [activeSection, setActiveSection] = useState('info');
  const [isEditing, setIsEditing] = useState(false);
  const [myEventsDeepLink, setMyEventsDeepLink] = useState(null);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // The creation wizard lands here after publishing, carrying the new event,
  // so the promoter gets the share pop-up straight away. Taken out of the
  // history entry at once so a reload or Back doesn't show it again.
  const [publishedEvent, setPublishedEvent] = useState(
    () => location.state?.publishedEvent || null
  );
  useEffect(() => {
    if (location.state?.publishedEvent) {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: null,
      });
    }
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { user, isAuthenticated } = useSelector((state) => state.auth);

  const {
    promoter,
    loading: promoterLoading,
    error: promoterError,
  } = useSelector((state) => state.promoter);

  const {
    brand,
    loading: brandLoading,
    error: brandError,
  } = useSelector((state) => state.brands);

  const notificationCount = useSelector(
    (state) => state.notifications.unreadCount || 0
  );

  const isBrand = user?.role === 'brand';
  const userInfo = isBrand ? brand : promoter;
  const userInfoLoading = isBrand ? brandLoading : promoterLoading;
  const userInfoError = isBrand ? brandError : promoterError;

  // Track whether we have ever successfully loaded the current user's profile.
  const [hasLoadedInitialProfile, setHasLoadedInitialProfile] = useState(false);

  useEffect(() => {
    setHasLoadedInitialProfile(false);
  }, [user?.id, user?.role]);

  useEffect(() => {
    if (userInfo) {
      setHasLoadedInitialProfile(true);
    }
  }, [userInfo]);

  // Read deep-link query params once per URL change
  const lastAppliedSearchRef = useRef(null);
  useEffect(() => {
    if (lastAppliedSearchRef.current === location.search) return;
    lastAppliedSearchRef.current = location.search;

    const qs = new URLSearchParams(location.search);
    const tab = (qs.get('tab') || '').toLowerCase();
    const openEventManager = qs.get('openEventManager') === '1';
    const emTab = (qs.get('emTab') || '').toLowerCase();
    const eventId = qs.get('eventId');

    if (tab === 'notifications') {
      setActiveSection('notifications');
    } else if (tab === 'info') {
      setActiveSection('info');
    } else if (tab === 'payment-details' || tab === 'paymentdetails') {
      // T2B-423: everything that tells a promoter to connect Easypay — the
      // header reminder, the "i" beside the create buttons — lands here, where
      // the connect CTA lives.
      setActiveSection('paymentDetails');
    } else if (tab === 'financials' || tab === 'financial-data') {
      setActiveSection('financials');
    } else if (tab === 'help' || tab === 'support') {
      // Notification emails point people here ("contact us through the
      // support page"), so the link has to resolve to the Help tab rather
      // than dropping them on their profile.
      setActiveSection('help');
    } else if (tab === 'my-events') {
      setActiveSection('myEvents');

      const list = (qs.get('list') || '').toLowerCase();
      if (openEventManager && eventId) {
        setMyEventsDeepLink({ openEventManager: true, eventId, emTab });
      } else if (list === 'drafts') {
        setMyEventsDeepLink({ list: 'drafts' });
      } else {
        setMyEventsDeepLink(null);
      }
    }
  }, [location.search]);

  // Fetch profile ONLY when the authenticated user changes.
  const fetchedProfileKeyRef = useRef(null);
  useEffect(() => {
    if (!user?.id || !user?.role) return;

    const fetchKey = `${user.role}:${user.id}`;
    if (fetchedProfileKeyRef.current === fetchKey) return;

    fetchedProfileKeyRef.current = fetchKey;

    if (user.role === 'brand') {
      dispatch(getBrand(user.id));
    } else {
      dispatch(getPromoter(user.id));
    }
  }, [dispatch, user?.id, user?.role]);

  // Fetch unread notifications once per auth session
  const fetchedUnreadRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated) {
      fetchedUnreadRef.current = false;
      return;
    }

    if (!fetchedUnreadRef.current) {
      fetchedUnreadRef.current = true;
      dispatch(getUnreadNotificationsCount());
    }
  }, [dispatch, isAuthenticated]);

  const handleSectionChange = useCallback((section) => {
    setActiveSection(section);
  }, []);

  const handleClearDeepLink = useCallback(() => {
    setMyEventsDeepLink(null);
  }, []);

  // The profile view, which is both the default tab and where any section a
  // given role can't see falls back to.
  const profileSection = useMemo(() => {
    if (isBrand) {
      return isEditing ? (
        <BrandInfoEdit brand={brand} exitEditMode={() => setIsEditing(false)} />
      ) : (
        <BrandInfo
          brand={brand}
          loading={brandLoading}
          error={brandError}
          onEdit={() => setIsEditing(true)}
        />
      );
    }

    return isEditing ? (
      <PromoterInfoEdit exitEditMode={() => setIsEditing(false)} />
    ) : (
      <PromoterInfo
        promoter={promoter}
        loading={promoterLoading}
        error={promoterError}
        onEdit={() => setIsEditing(true)}
      />
    );
  }, [
    isBrand,
    isEditing,
    brand,
    brandLoading,
    brandError,
    promoter,
    promoterLoading,
    promoterError,
  ]);

  const renderSection = useMemo(() => {
    switch (activeSection) {
      case 'notifications':
        return <Notifications />;

      case 'messages':
        return <Messages />;

      case 'myEvents':
        return <MyEvents user={user} deepLink={myEventsDeepLink} onClearDeepLink={handleClearDeepLink} />;

      case 'paymentDetails':
        return <PaymentDetails />;

      case 'financials':
        // Promoters only — the endpoint behind it is scoped to them, and a
        // brand landing here would just get a 403 card. The sidebar never
        // offers it to a brand; this covers a hand-typed ?tab=financials.
        // Handed the section setter so its "connect Easypay" CTA can send the
        // promoter to the tab that owns that flow.
        return isBrand ? (
          profileSection
        ) : (
          <Financials onSectionChange={handleSectionChange} />
        );

      case 'preferences':
        return <Preferences />;

      case 'analytics':
        return <Analytics />;

      case 'securityPrivacy':
        return <SecurityPrivacy />;

      case 'help':
        return <Help />;

      default:
        return profileSection;
    }
  }, [
    activeSection,
    isBrand,
    user,
    myEventsDeepLink,
    handleClearDeepLink,
    handleSectionChange,
    profileSection,
  ]);

  // Only block the whole page on the VERY FIRST load for this user.
  if (!hasLoadedInitialProfile && userInfoLoading && !userInfo) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <ClipLoader color="#FFBD59" size={40} />
      </div>
    );
  }

  // Optional error state before first successful load only.
  if (!hasLoadedInitialProfile && !userInfo && userInfoError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-6">
        <p className="text-red-600 font-semibold mb-2">
          Failed to load profile.
        </p>
        <p className="text-gray-600 text-sm">{String(userInfoError)}</p>
      </div>
    );
  }

  // Redesigned tabs carry their own TabHeader inside the main card, so the
  // legacy BoardHeader is hidden on them. Tabs not yet redesigned still use it.
  const REDESIGNED_TABS = new Set([
    'info',
    'notifications',
    'myEvents',
    'paymentDetails',
    'financials',
    'preferences',
    'securityPrivacy',
    'help',
  ]);
  const showBoardHeader = !REDESIGNED_TABS.has(activeSection);

  return (
    <div className="min-h-screen bg-gray-100 p-4 flex flex-col lg:flex-row items-start lg:gap-6 gap-3 relative">
      {/* Keep dashboard mounted even if profile refetches later */}
      {hasLoadedInitialProfile && userInfoLoading && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/50 backdrop-blur-[1px]">
          <ClipLoader color="#FFBD59" size={32} />
        </div>
      )}

      <ShareEventModal
        event={publishedEvent}
        justPublished
        onClose={() => setPublishedEvent(null)}
      />

      <div className="w-full lg:w-[288px] lg:sticky lg:top-4">
        <Sidebar
          user={user?.role}
          info={userInfo}
          onSectionChange={handleSectionChange}
          activeSection={activeSection}
          notificationCount={notificationCount}
          paymentDetailsAlert={!isBrand && !!promoter && !hasEasypayPayoutAccount(promoter)}
        />
      </div>

      <div className="flex-1 w-full min-w-0">
        {showBoardHeader && (
          <BoardHeader
            info={userInfo}
            section={activeSection}
            userType={user?.role}
            setIsEditing={setIsEditing}
            isEditing={isEditing}
          />
        )}

        <main
          className={`w-full bg-white shadow-card border border-line/60 p-6 sm:p-8 md:p-10 min-w-0 ${
            showBoardHeader ? 'rounded-b-3xl' : 'rounded-3xl'
          }`}
        >
          {renderSection}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
